import { Button, Divider, Space, Typography } from 'antd';
import { MobileOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePublicSurveyParticipation } from '../../hooks/usePublicSurveyParticipation';
import { getReferralCode } from '../../utils/referral-cookie';
import { useGuestSession } from '../../services/useGuestSession';

const { Text } = Typography;

interface SurveyLandingCTAProps {
  surveyId: string;
  surveyHash: string;
  hasUserParticipated: boolean;
  status: 'ACTIVE' | 'COMPLETED';
  allowGuestParticipation?: boolean;
  showAuditTrailLink?: boolean;
}

export function SurveyLandingCTA({
  surveyId,
  surveyHash,
  hasUserParticipated,
  status,
  allowGuestParticipation = false,
  showAuditTrailLink = true,
}: SurveyLandingCTAProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { startParticipation } = usePublicSurveyParticipation();
  const guestSession = useGuestSession();

  const auditTrailUrl = `/public/surveys/${surveyHash}/audit-trail`;

  const handleLogin = () => {
    navigate('/login', {
      state: { redirect: `/public/surveys/${surveyHash}` },
    });
  };

  const handleRegister = () => {
    const ref = getReferralCode();
    const url = ref ? `/signup?ref=${encodeURIComponent(ref)}` : '/signup';
    navigate(url);
  };

  const handleParticipate = () => {
    startParticipation(surveyId);
  };

  const handleGuestParticipate = () => {
    // Passa surveyId numérico via state para que SurveyPhoneOtpPage possa navegar de volta
    navigate(`/public/surveys/${surveyHash}/participar-telefone`, {
      state: { surveyId },
    });
  };

  const handleGuestContinue = () => {
    // Convidado com sessão ativa: vai direto para responder (sem ProtectedRoute)
    navigate(`/surveys/${surveyId}/guest-take`);
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {isAuthenticated ? (
          <>
            {status === 'ACTIVE' && !hasUserParticipated && (
              <Button type="primary" size="large" block onClick={handleParticipate}>
                Participar
              </Button>
            )}
            {(status === 'COMPLETED' || hasUserParticipated) && (
              <Button size="large" block href={auditTrailUrl}>
                Ver Resultado
              </Button>
            )}
          </>
        ) : (
          <>
            {status === 'ACTIVE' && (
              <>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="primary" size="large" style={{ flex: 1 }} onClick={handleLogin}>
                    Entrar
                  </Button>
                  <Button size="large" style={{ flex: 1 }} onClick={handleRegister}>
                    Criar Conta
                  </Button>
                </div>
                {allowGuestParticipation ? (
                  guestSession.isActive ? (
                    <Button
                      size="large"
                      block
                      icon={<MobileOutlined />}
                      onClick={handleGuestContinue}
                    >
                      Continuar com telefone verificado
                    </Button>
                  ) : (
                    <Button
                      size="large"
                      block
                      icon={<MobileOutlined />}
                      onClick={handleGuestParticipate}
                    >
                      Participar com código por SMS
                    </Button>
                  )
                ) : (
                  <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>
                    Esta pesquisa não permite participação apenas com validação de código SMS.
                  </Text>
                )}
              </>
            )}
          </>
        )}

        {showAuditTrailLink && (
          <>
            <Divider style={{ margin: '8px 0' }} />

            <Button type="link" href={auditTrailUrl} block>
              Ver transparência pública desta pesquisa
            </Button>
          </>
        )}
      </Space>
    </div>
  );
}
